import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Filter,
  Image as ImageIcon,
  Maximize2,
  Move,
  QrCode,
  Rss,
  Settings2,
  Upload,
} from 'lucide-react';
import {
  feedBrandToTemplateBrand,
  type ListingListResponse,
  type UploadedFontFace,
} from '@proptr/shared';
import { ApiError, type Feed, type FeedInput } from '@proptr/api-client';
import { useAuth } from '@proptr/api-client/react';
import {
  BANNER_RATIOS,
  BANNER_RATIO_LABEL,
  type BannerRatio,
  DEFAULT_BRAND,
  ListingBanner,
  MARK_DEFAULT,
  MARK_MAX,
  MARK_MIN,
  QR_DEFAULT,
  bannerQrDefault,
  QR_MAX,
  QR_MIN,
  bannerHeight,
  bannerHeroHeight,
} from '@proptr/creative-templates';
import {
  LOOKBOOK_DEFAULT_BRAND,
  LOOKBOOK_QR_DEFAULT_POS,
  ListingLookbook,
} from '@proptr/creative-templates';
import { FeedFullPreview } from '../components/FeedFullPreview.js';
import { PublishToMetaModal } from '../components/PublishToMetaModal.js';
import {
  FONT_OPTIONS,
  FontField,
  brandFontFamily,
  useUploadedFonts,
} from '../components/FontField.js';
import { SectionCard } from '../components/SectionCard.js';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { ColorField } from '../components/ui/color-field.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { MultiSelect } from '../components/ui/multi-select.js';
import { Select, type SelectOption } from '../components/ui/select.js';
import { Slider } from '../components/ui/slider.js';
import { Switch } from '../components/ui/switch.js';
import { Tooltip } from '../components/ui/tooltip.js';
import { bannerListingFrom } from '../lib/bannerListing.js';
import { resolveFeedUrl } from '../lib/feedUrl.js';
import { qrFromPointer, qrHandleStyle } from '../lib/qrDrag.js';
import { TEMPLATE_OPTIONS } from '../lib/creativeTemplates.js';
import { isTestMode } from '../lib/testMode.js';
import { cn } from '../lib/utils.js';
import { uploadImage } from '../lib/uploadImage.js';
import { client } from '../main.js';

// Split view mirroring the proptree settings pages: the feed's settings
// on the left, a live preview of what it will contain on the right. The
// preview is driven entirely by the current form state, so narrowing the
// filters updates the listing set without a round-trip.

type TypeFilter = 'all' | 'sale' | 'rent';
type PlanFilter = 'all' | 'on_plan' | 'off_plan';
type Format = 'meta' | 'whatsapp';

const TYPE_OPTIONS: SelectOption<TypeFilter>[] = [
  { value: 'all', label: 'Sale & Rentals' },
  { value: 'sale', label: 'Sale only' },
  { value: 'rent', label: 'Rentals only' },
];

const PLAN_OPTIONS: SelectOption<PlanFilter>[] = [
  { value: 'all', label: 'On-plan & Off-plan' },
  { value: 'on_plan', label: 'On-plan' },
  { value: 'off_plan', label: 'Off-plan' },
];

// Both are CSV; what differs is the catalog vertical, and the two can't
// be merged — WhatsApp only renders `products`, and only `home_listings`
// carries the address, geo and placement tags Advantage+ needs.
const FORMAT_OPTIONS: SelectOption<Format>[] = [
  { value: 'meta', label: 'Meta catalog (CSV)' },
  { value: 'whatsapp', label: 'WhatsApp catalog (CSV)' },
];


// Web-safe plus the fonts the brand kit already leans on. A brokerage
// with a licensed typeface uploads it instead.
// Value is the CSS stack the template renders with; the family is fetched
// on demand when picked (see lib/googleFont.ts), so a long list costs

interface FeedForm {
  name: string;
  template: string;
  /** Emit a 9:16 creative per listing alongside the square one. */
  includeVertical: boolean;
  /** Feed-scoped logo override. null = inherit the agency logo. Uploading
   *  here never writes back to the agency's own logo. */
  logoUrl: string | null;
  logoEnabled: boolean;
  logoSize: number;
  qrEnabled: boolean;
  qrSize: number;
  /** Saved QR placement per ratio, normalised 0–1 against that ratio's
   *  photo area. null = that template's default. */
  qrPosition: { x: number; y: number } | null;
  qrPositionVertical: { x: number; y: number } | null;
  mainColor: string;
  secondaryColor: string;
  mainFont: string;
  secondaryFont: string;
  /** An uploaded typeface, in place of the dropdown selection. `family`
   *  is generated (see `fontFamilyFor`) so two brokerages uploading the
   *  same filename can't collide in the render page's FontFace registry;
   *  `url` is the CDN location the render page loads it from. */
  mainFontFace: UploadedFontFace | null;
  secondaryFontFace: UploadedFontFace | null;
  type: TypeFilter;
  plan: PlanFilter;
  /** Empty array = no restriction. */
  areas: string[];
  propertyTypes: string[];
  bedrooms: string[];
  agents: string[];
  minPrice: string;
  maxPrice: string;
  format: Format;
}

// Exact counts, not "N+" — with multi-select the user composes their own
// range by ticking several, which a set of overlapping minimums couldn't
// express.
const BEDROOM_OPTIONS: SelectOption<string>[] = [
  { value: '0', label: 'Studio' },
  { value: '1', label: '1 bedroom' },
  { value: '2', label: '2 bedrooms' },
  { value: '3', label: '3 bedrooms' },
  { value: '4', label: '4 bedrooms' },
  { value: '5', label: '5+ bedrooms' },
];

const EMPTY_FORM: FeedForm = {
  name: 'Meta catalog feed',
  template: 'listing-banner',
  // On by default: a catalog with only square creatives gets centre-cropped
  // into every vertical placement, which is the whole problem 9:16 solves.
  includeVertical: true,
  logoUrl: null,
  logoEnabled: true,
  logoSize: MARK_DEFAULT,
  // On by default — Dubai requires the permit QR on advertised creative.
  qrEnabled: true,
  qrSize: QR_DEFAULT,
  qrPosition: null,
  qrPositionVertical: null,
  mainColor: '#6C5CE7',
  secondaryColor: '#0F172A',
  mainFont: "'Jost', system-ui, sans-serif",
  secondaryFont: "'Geist Mono', ui-monospace, monospace",
  mainFontFace: null,
  secondaryFontFace: null,
  type: 'all',
  plan: 'all',
  areas: [],
  propertyTypes: [],
  bedrooms: [],
  agents: [],
  minPrice: '',
  maxPrice: '',
  format: 'meta',
};

// === Persistence mapping ============================================
// The form is flat; the feed document nests brand and filters. These two
// convert between them. Anything the document doesn't carry falls back to
// the same defaults a new feed starts from, so an older doc saved before a
// field existed still hydrates cleanly.
//
// Uploaded typefaces round-trip as { family, url } pairs — the family is
// what the render page registers with the FontFace API, the url is what it
// loads. See `fontFamilyFor` for why the family is generated.
function formFromFeed(feed: Feed): FeedForm {
  const brand = feed.brand ?? ({} as Partial<Feed['brand']>);
  const filters = feed.filters ?? ({} as Partial<Feed['filters']>);
  return {
    ...EMPTY_FORM,
    name: feed.name || EMPTY_FORM.name,
    template: feed.template || EMPTY_FORM.template,
    includeVertical: feed.includeVertical ?? EMPTY_FORM.includeVertical,
    logoUrl: brand.logoUrl ?? null,
    logoEnabled: brand.logoEnabled ?? EMPTY_FORM.logoEnabled,
    logoSize: brand.logoSize ?? EMPTY_FORM.logoSize,
    qrEnabled: brand.qrEnabled ?? EMPTY_FORM.qrEnabled,
    qrSize: brand.qrSize ?? EMPTY_FORM.qrSize,
    qrPosition: brand.qrPosition ?? null,
    qrPositionVertical: brand.qrPositionVertical ?? null,
    mainColor: brand.mainColor ?? EMPTY_FORM.mainColor,
    secondaryColor: brand.secondaryColor ?? EMPTY_FORM.secondaryColor,
    mainFont: brand.mainFont ?? EMPTY_FORM.mainFont,
    secondaryFont: brand.secondaryFont ?? EMPTY_FORM.secondaryFont,
    mainFontFace: brand.mainFontFace ?? null,
    secondaryFontFace: brand.secondaryFontFace ?? null,
    type: filters.type ?? EMPTY_FORM.type,
    plan: filters.plan ?? EMPTY_FORM.plan,
    areas: filters.areas ?? [],
    propertyTypes: filters.propertyTypes ?? [],
    bedrooms: filters.bedrooms ?? [],
    agents: filters.agents ?? [],
    // Stored as numbers; the inputs are text.
    minPrice: filters.minPrice == null ? '' : String(filters.minPrice),
    maxPrice: filters.maxPrice == null ? '' : String(filters.maxPrice),
    // A feed saved before the format list narrowed can still carry a
    // value the Select has no option for, which would render an empty
    // control and then save that value straight back. Fall back instead.
    format: FORMAT_OPTIONS.some((o) => o.value === feed.format)
      ? (feed.format as Format)
      : EMPTY_FORM.format,
  };
}

function feedInputFromForm(form: FeedForm): FeedInput {
  const price = (v: string) => {
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };
  return {
    name: form.name.trim() || 'Untitled feed',
    template: form.template,
    includeVertical: form.includeVertical,
    brand: {
      logoUrl: form.logoUrl,
      logoEnabled: form.logoEnabled,
      logoSize: form.logoSize,
      qrEnabled: form.qrEnabled,
      qrSize: form.qrSize,
      qrPosition: form.qrPosition,
      qrPositionVertical: form.qrPositionVertical,
      mainColor: form.mainColor,
      secondaryColor: form.secondaryColor,
      mainFont: form.mainFont,
      secondaryFont: form.secondaryFont,
      mainFontFace: form.mainFontFace,
      secondaryFontFace: form.secondaryFontFace,
    },
    filters: {
      type: form.type,
      plan: form.plan,
      areas: form.areas,
      propertyTypes: form.propertyTypes,
      bedrooms: form.bedrooms,
      agents: form.agents,
      minPrice: price(form.minPrice),
      maxPrice: price(form.maxPrice),
    },
    format: form.format,
  };
}

// Font-family names for uploaded typefaces.
//
// Generated, never derived from the filename. The render page registers
// every uploaded face into one global FontFace registry keyed by family
// name, so if the family came from the file, two brokerages both
// uploading "Regular.woff2" would claim the same name — and whichever
// creative rendered second would come out in the other tenant's
// typeface. Scoping by feed id makes that impossible.
//

/** The public feed URL, resolved against the origin the API lives on. */
export function FeedEditPage() {
  // /marketing/feed/new has no param — the static route wins over :id.
  const { id: routeId } = useParams<{ id: string }>();
  const feedId = routeId && routeId !== 'new' ? routeId : null;
  const navigate = useNavigate();

  // The banner's lockup uses the brokerage's own name and logo.
  const { enterprise } = useAuth();
  const [form, setForm] = React.useState<FeedForm>(EMPTY_FORM);
  const set = <K extends keyof FeedForm>(key: K, value: FeedForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // The persisted document, once there is one. Source of the feed URL.
  const [saved, setSaved] = React.useState<Feed | null>(null);
  const savedRef = React.useRef<Feed | null>(null);
  savedRef.current = saved;
  const [loading, setLoading] = React.useState(feedId !== null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [notFound, setNotFound] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [publishOpen, setPublishOpen] = React.useState(false);

  // Hydrate from the feeds list — the API has no single-feed GET, and the
  // list is enterprise-scoped so it stays small.
  React.useEffect(() => {
    if (!feedId) {
      setSaved(null);
      setLoading(false);
      return;
    }
    // Just created: the POST response already hydrated the form, and the
    // navigate to /:id is what re-ran this. Don't refetch it.
    if (savedRef.current?._id === feedId) return;
    let alive = true;
    setLoading(true);
    setLoadError(null);
    setNotFound(false);
    client.feeds
      .list()
      .then((res) => {
        if (!alive) return;
        const doc = res.items.find((f) => f._id === feedId);
        if (!doc) {
          setNotFound(true);
          return;
        }
        setSaved(doc);
        setForm(formFromFeed(doc));
      })
      .catch((err: unknown) => {
        if (alive) setLoadError((err as Error).message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [feedId]);

  const handleSave = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const input = feedInputFromForm(form);
      if (feedId) {
        const doc = await client.feeds.update(feedId, input);
        setSaved(doc);
        setForm(formFromFeed(doc));
      } else {
        const doc = await client.feeds.create(input);
        // Set before navigating so the load effect knows to skip its fetch.
        savedRef.current = doc;
        setSaved(doc);
        setForm(formFromFeed(doc));
        navigate(`/marketing/feed/${doc._id}`, { replace: true });
        // Offer the Meta step immediately: the feed now has a public URL,
        // which is the only thing that had to exist first. Still a
        // separate, declinable action — publishing writes a catalog into
        // the brokerage's Meta business, which shouldn't be a side effect
        // of pressing Save.
        setPublishOpen(true);
      }
    } catch (err) {
      setSaveError(
        err instanceof ApiError ? err.message : (err as Error).message,
      );
    } finally {
      setSaving(false);
    }
  };

  // The banner's mark. Defaults to the agency logo and is only overridden
  // for this feed — the agency record is never touched.
  const agencyLogo = enterprise?.logo ?? null;
  const effectiveLogo = form.logoUrl ?? agencyLogo;
  const [logoBusy, setLogoBusy] = React.useState(false);
  const [logoError, setLogoError] = React.useState<string | null>(null);
  const logoInputRef = React.useRef<HTMLInputElement>(null);

  const handleLogoFile = async (file: File) => {
    setLogoBusy(true);
    setLogoError(null);
    try {
      const url = await uploadImage({ file, kind: 'agency-logo' });
      set('logoUrl', url);
    } catch (err) {
      setLogoError((err as Error).message);
    } finally {
      setLogoBusy(false);
    }
  };

  // Stable for the lifetime of this editor, so an upload made before the
  // feed is first saved still gets a unique family. See `fontFamilyFor`.
  const sessionToken = React.useMemo(
    () => Math.random().toString(36).slice(2, 10),
    [],
  );

  // Uploads the file to Spaces and stores the { family, url } pair. The
  // font is registered under the generated family by the render page and,
  // for the live preview, by `useUploadedFonts` below.
  const handleFontFile = async (
    slot: 'main' | 'secondary',
    file: File,
  ): Promise<void> => {
    const key = slot === 'main' ? 'mainFontFace' : 'secondaryFontFace';
    const url = await uploadImage({ file, kind: 'brand-font' });
    set(key, {
      family: brandFontFamily(
        'feed',
        feedId,
        sessionToken,
        slot === 'main' ? 'display' : 'mono',
      ),
      url,
    });
  };

  // The listing set the preview draws from. Fetched once; the filters
  // narrow it client-side so the preview reacts instantly.
  const [listings, setListings] = React.useState<
    ListingListResponse['items'] | null
  >(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    client.listings
      .list({ status: 'active', type: 'all', page: 1, limit: 200 })
      .then((res) => {
        if (alive) setListings(res.items);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, []);

  const matched = React.useMemo(() => {
    if (!listings) return null;
    const min = Number.parseInt(form.minPrice, 10);
    const max = Number.parseInt(form.maxPrice, 10);
    return listings.filter((l) => {
      if (form.type === 'sale' && !l.forSale) return false;
      if (form.type === 'rent' && !l.forRent) return false;
      // An empty list means "no restriction" for each of these.
      if (form.areas.length && !form.areas.includes(l.area ?? '')) return false;
      if (
        form.propertyTypes.length &&
        !form.propertyTypes.includes(l.propertyType ?? '')
      ) {
        return false;
      }
      if (form.agents.length && !form.agents.includes(l.agent?._id ?? ''))
        return false;
      if (form.bedrooms.length) {
        const n = l.bedrooms ?? 0;
        // '5' is the open-ended bucket; the rest match exactly.
        const hit = form.bedrooms.some((b) =>
          b === '5' ? n >= 5 : n === Number(b),
        );
        if (!hit) return false;
      }
      if (Number.isFinite(min) && (l.price ?? 0) < min) return false;
      if (Number.isFinite(max) && (l.price ?? 0) > max) return false;
      return true;
    });
  }, [
    listings,
    form.type,
    form.areas,
    form.propertyTypes,
    form.agents,
    form.bedrooms,
    form.minPrice,
    form.maxPrice,
  ]);

  // Option lists come from the listings themselves, so a brokerage only
  // ever sees filters that can actually match something.
  const areaOptions: SelectOption<string>[] = React.useMemo(() => {
    const areas = [
      ...new Set((listings ?? []).map((l) => l.area).filter(Boolean)),
    ].sort() as string[];
    return areas.map((a) => ({ value: a, label: a }));
  }, [listings]);

  const propertyTypeOptions: SelectOption<string>[] = React.useMemo(() => {
    const types = [
      ...new Set((listings ?? []).map((l) => l.propertyType).filter(Boolean)),
    ].sort() as string[];
    return types.map((t) => ({ value: t, label: t }));
  }, [listings]);

  const agentOptions: SelectOption<string>[] = React.useMemo(() => {
    const seen = new Map<string, string>();
    for (const l of listings ?? []) {
      if (l.agent?._id) seen.set(l.agent._id, l.agent.name ?? l.agent._id);
    }
    return [...seen.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [listings]);

  // Only exists once the feed has been saved — the publicId is minted
  // server-side.
  const feedUrl = saved ? resolveFeedUrl(saved.feedPath) : null;

  return (
    <AppShell>
      <PublishToMetaModal
        open={publishOpen}
        feed={saved}
        onClose={() => setPublishOpen(false)}
        onPublished={() => {
          // Re-read so the page reflects the new catalog if the manager
          // stays here rather than going back to the list.
          void client.feeds.list().then((res) => {
            const doc = res.items.find((f) => f._id === saved?._id);
            if (doc) setSaved(doc);
          });
        }}
      />
      <div className="space-y-6 pb-10">
        <div className="min-w-0">
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            {feedId ? saved?.name || 'Feed' : 'Create feed'}
          </h1>
          <div className="mt-3 flex items-center gap-3">
            <Link
              to="/marketing"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to marketing
            </Link>
            <span className="text-muted-foreground/50">·</span>
            <div className="eyebrow">Marketing / Feed</div>
          </div>
        </div>

        {loadError && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
            {loadError}
          </Card>
        )}

        {notFound && (
          <Card className="p-6">
            <p className="font-sans text-[13px] text-muted-foreground">
              No feed with that id exists, or you don't have access to it.
            </p>
          </Card>
        )}

        {loading && !loadError && !notFound && (
          <Card className="p-6 font-mono text-[11px] text-muted-foreground">
            Loading…
          </Card>
        )}

        {!loading && !loadError && !notFound && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Settings */}
          <div className="space-y-4">
            <SectionCard
              title="Identity"
              subtitle="What this feed is called"
              icon={Rss}
              defaultOpen
            >
              <div className="grid grid-cols-12 gap-4 p-5">
                <div className="col-span-12">
                  <Label htmlFor="feed-name">Feed name</Label>
                  <Input
                    id="feed-name"
                    className="mt-1"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    Only you see this — it identifies the feed in the list.
                  </p>
                </div>

                <div className="col-span-12 sm:col-span-6">
                  <Label>Template</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={TEMPLATE_OPTIONS}
                    value={form.template}
                    onChange={(v) => set('template', v)}
                    searchable={false}
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    The layout each listing card uses in the feed.
                  </p>
                </div>

                <div className="col-span-12 sm:col-span-6">
                  <Label>Vertical format</Label>
                  {/* h-11 matches the Select beside it so the two cells
                      line up on the same baseline. */}
                  <div
                    className={cn(
                      'mt-1 flex h-11 items-center justify-between gap-4 rounded-md border border-border bg-muted/30 px-3.5 dark:border-white/10 dark:bg-card-raised',
                      form.template === 'listing-lookbook' && 'opacity-50',
                    )}
                  >
                    <Label
                      htmlFor="feed-include-vertical"
                      className="mb-0 whitespace-nowrap"
                    >
                      Include 9:16
                    </Label>
                    {/* The lookbook has no vertical canvas, so the switch
                        would promise an export that can't be produced. */}
                    <Switch
                      id="feed-include-vertical"
                      checked={
                        form.includeVertical &&
                        form.template !== 'listing-lookbook'
                      }
                      disabled={form.template === 'listing-lookbook'}
                      onCheckedChange={(v) => set('includeVertical', v)}
                      aria-label="Include the 9:16 vertical format in this feed"
                    />
                  </div>
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    {form.template === 'listing-lookbook'
                      ? 'This template is square only.'
                      : form.includeVertical
                        ? 'Each listing also exports a 9:16 creative, sent as an additional image for stories and reels.'
                        : 'Square only. Meta will centre-crop it into vertical placements.'}
                  </p>
                </div>

                {/* Template is the structural choice; everything below it
                    is the brand kit applied to that template. */}
                <div className="col-span-12">
                  {/* -mx-5 cancels the grid's p-5 so the rule meets the
                      card edges instead of floating inside the padding. */}
                  <div className="-mx-5 border-t border-[hsl(var(--hairline))]" />
                </div>
                {/* Logo on the left, permit QR on the right — two
                    independent marks, each with its own toggle and size. */}
                <div className="col-span-12 sm:col-span-6">
                  <Label>Logo</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/30">
                      {effectiveLogo ? (
                        <img
                          src={effectiveLogo}
                          alt=""
                          className="h-full w-full object-contain p-1.5"
                        />
                      ) : (
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={logoBusy}
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <Upload />
                      {logoBusy ? 'Uploading…' : 'Replace'}
                    </Button>
                    {form.logoUrl && (
                      <button
                        type="button"
                        onClick={() => set('logoUrl', null)}
                        className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground transition-colors hover:text-foreground"
                      >
                        Reset
                      </button>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/svg+xml,image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleLogoFile(f);
                        e.target.value = '';
                      }}
                    />
                  </div>
                  <p className="mt-1.5 min-h-[2rem] font-mono text-[10px] leading-relaxed text-muted-foreground">
                    {logoError ? (
                      <span className="text-destructive">{logoError}</span>
                    ) : form.logoUrl ? (
                      'Custom mark for this feed only — your agency logo is unchanged. Reset to go back to it.'
                    ) : (
                      'Using your agency logo. Replace it to use a different mark for this feed only.'
                    )}
                  </p>

                  <div className="mt-4 flex items-center justify-between gap-4">
                    <Label htmlFor="feed-logo-enabled" className="mb-0">
                      Show logo
                    </Label>
                    <Switch
                      id="feed-logo-enabled"
                      checked={form.logoEnabled}
                      onCheckedChange={(v) => set('logoEnabled', v)}
                      aria-label="Show logo on the creative"
                    />
                  </div>

                  <div className="mt-3">
                    <Label htmlFor="feed-logo-size">Logo size</Label>
                    <Slider
                      id="feed-logo-size"
                      className="mt-2"
                      min={MARK_MIN}
                      max={MARK_MAX}
                      step={4}
                      value={form.logoSize}
                      disabled={!form.logoEnabled}
                      onChange={(v) => set('logoSize', v)}
                      valueLabel={`${form.logoSize} px`}
                    />
                  </div>
                </div>

                <div className="col-span-12 sm:col-span-6 sm:border-l sm:border-[hsl(var(--hairline))] sm:pl-5">
                  <Label>Permit QR</Label>
                  <div className="mt-1 flex h-14 items-center">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-border bg-muted/30">
                      <QrCode className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                  <p className="mt-1.5 min-h-[2rem] font-mono text-[10px] leading-relaxed text-muted-foreground">
                    Dubai requires the Trakheesi QR on advertised listings.
                    Only shown for listings that carry one.
                  </p>

                  <div className="mt-4 flex items-center justify-between gap-4">
                    <Label htmlFor="feed-qr-enabled" className="mb-0">
                      Show permit QR
                    </Label>
                    <Switch
                      id="feed-qr-enabled"
                      checked={form.qrEnabled}
                      onCheckedChange={(v) => set('qrEnabled', v)}
                      aria-label="Show the permit QR on the creative"
                    />
                  </div>

                  <div className="mt-3">
                    <Label htmlFor="feed-qr-size">QR size</Label>
                    <Slider
                      id="feed-qr-size"
                      className="mt-2"
                      min={QR_MIN}
                      max={QR_MAX}
                      step={4}
                      value={form.qrSize}
                      disabled={!form.qrEnabled}
                      onChange={(v) => set('qrSize', v)}
                      valueLabel={`${form.qrSize} px`}
                    />
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="-mx-5 border-t border-[hsl(var(--hairline))]" />
                </div>

                <div className="col-span-12 sm:col-span-6 sm:col-start-1">
                  <ColorField
                    id="feed-main-color"
                    label="Main color"
                    labelClassName=""
                    value={form.mainColor}
                    onChange={(v) => set('mainColor', v)}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <ColorField
                    id="feed-secondary-color"
                    label="Secondary color"
                    labelClassName=""
                    value={form.secondaryColor}
                    onChange={(v) => set('secondaryColor', v)}
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    Tints the shade behind the price. Keep it dark — the
                    white type sits on top of it.
                  </p>
                </div>

                <div className="col-span-12 sm:col-span-6 sm:col-start-1">
                  <FontField
                    id="feed-main-font"
                    label="Main font"
                    value={form.mainFont}
                    face={form.mainFontFace}
                    onChange={(v) => set('mainFont', v)}
                    onUpload={(file) => handleFontFile('main', file)}
                    onClear={() => set('mainFontFace', null)}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <FontField
                    id="feed-secondary-font"
                    label="Secondary font"
                    value={form.secondaryFont}
                    face={form.secondaryFontFace}
                    onChange={(v) => set('secondaryFont', v)}
                    onUpload={(file) => handleFontFile('secondary', file)}
                    onClear={() => set('secondaryFontFace', null)}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Listings"
              subtitle="Which of your listings this feed includes"
              icon={Filter}
              defaultOpen
            >
              <div className="grid grid-cols-12 gap-4 p-5">
                <div className="col-span-12 sm:col-span-6">
                  <Label>Type</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={TYPE_OPTIONS}
                    value={form.type}
                    onChange={(v) => set('type', v)}
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    Restrict the feed to sale-only, rental-only, or both.
                  </p>
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Plan</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={PLAN_OPTIONS}
                    value={form.plan}
                    onChange={(v) => set('plan', v)}
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    Restrict the feed to on-plan, off-plan, or both.
                  </p>
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Area</Label>
                  <MultiSelect
                    className="mt-1 bg-muted/30"
                    options={areaOptions}
                    value={form.areas}
                    onChange={(v) => set('areas', v)}
                    allLabel="All areas"
                    itemNoun="areas"
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Property type</Label>
                  <MultiSelect
                    className="mt-1 bg-muted/30"
                    options={propertyTypeOptions}
                    value={form.propertyTypes}
                    onChange={(v) => set('propertyTypes', v)}
                    allLabel="All property types"
                    itemNoun="types"
                    searchable={false}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Bedrooms</Label>
                  <MultiSelect
                    className="mt-1 bg-muted/30"
                    options={BEDROOM_OPTIONS}
                    value={form.bedrooms}
                    onChange={(v) => set('bedrooms', v)}
                    allLabel="Any bedrooms"
                    itemNoun="sizes"
                    searchable={false}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Agent</Label>
                  <MultiSelect
                    className="mt-1 bg-muted/30"
                    options={agentOptions}
                    value={form.agents}
                    onChange={(v) => set('agents', v)}
                    allLabel="All agents"
                    itemNoun="agents"
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    Limit the feed to specific agents' listings.
                  </p>
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label htmlFor="feed-min-price">Min price</Label>
                  <Input
                    id="feed-min-price"
                    className="mt-1 bg-muted/30"
                    inputMode="numeric"
                    placeholder="No minimum"
                    value={form.minPrice}
                    onChange={(e) =>
                      set('minPrice', e.target.value.replace(/[^\d]/g, ''))
                    }
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label htmlFor="feed-max-price">Max price</Label>
                  <Input
                    id="feed-max-price"
                    className="mt-1 bg-muted/30"
                    inputMode="numeric"
                    placeholder="No maximum"
                    value={form.maxPrice}
                    onChange={(e) =>
                      set('maxPrice', e.target.value.replace(/[^\d]/g, ''))
                    }
                  />
                </div>

                {/* The filters above are abstract until you know what they
                    select. Without this the only feedback was the preview
                    column, which you have to scroll to count. */}
                <div className="col-span-12">
                  <div className="-mx-5 border-t border-[hsl(var(--hairline))]" />
                </div>
                <div className="col-span-12 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-display text-[15px] font-medium leading-none">
                      {matched === null
                        ? 'Counting…'
                        : matched.length === 1
                          ? '1 listing matches'
                          : `${matched.length} listings match`}
                    </div>
                    <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                      {matched === null
                        ? 'Loading your listings.'
                        : matched.length === 0
                          ? 'The feed would be empty — Meta rejects a catalog with no items.'
                          : form.includeVertical &&
                              form.template !== 'listing-lookbook'
                            ? `${matched.length * 2} creatives: one square and one 9:16 per listing.`
                            : `${matched.length} creatives, one per listing.`}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-md px-3 py-1.5 font-mono text-[13px] tabular-nums',
                      matched?.length
                        ? 'bg-muted/50 text-foreground'
                        : 'bg-destructive/10 text-destructive',
                    )}
                  >
                    {matched === null ? '—' : matched.length}
                  </span>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Delivery"
              subtitle="Format and the URL to hand to Meta"
              icon={Settings2}
            >
              <div className="grid grid-cols-12 gap-4 p-5">
                <div className="col-span-12 sm:col-span-6">
                  <Label>Format</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={FORMAT_OPTIONS}
                    value={form.format}
                    onChange={(v) => set('format', v)}
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    The feed is built from your listings on every fetch, so it
                    is never out of date.
                  </p>
                </div>
                <div className="col-span-12">
                  <Label>Feed URL</Label>
                  <div
                    className={cn(
                      'mt-1 truncate rounded-md border border-border bg-muted/30 px-3 py-2.5 font-mono text-[12px]',
                      feedUrl ? 'text-foreground/80' : 'text-muted-foreground',
                    )}
                    title={feedUrl ?? undefined}
                  >
                    {feedUrl ?? 'https://proptr.ee/feeds/…'}
                  </div>
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    {feedUrl
                      ? 'Paste this into Meta Commerce Manager as a scheduled feed.'
                      : 'Generated once the feed is created. Paste this into Meta Commerce Manager as a scheduled feed.'}
                  </p>
                </div>
              </div>
            </SectionCard>

            <div className="flex items-center justify-end gap-3">
              {saveError && (
                <span className="font-mono text-[11px] text-destructive">
                  {saveError}
                </span>
              )}
              {/* Test mode never writes — same lock as the form and
                  proptree editors. */}
              {isTestMode() ? (
                <Tooltip
                  label={`Can't ${feedId ? 'save' : 'create'} a feed in test mode`}
                  className="cursor-not-allowed"
                >
                  <Button size="sm" disabled>
                    {feedId ? 'Save changes' : 'Create feed'}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  size="sm"
                  disabled={saving}
                  onClick={() => void handleSave()}
                >
                  {saving
                    ? 'Saving…'
                    : feedId
                      ? 'Save changes'
                      : 'Create feed'}
                </Button>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="lg:sticky lg:top-[calc(var(--header-h,4rem)+var(--banner-h,0px)+0.5rem)] lg:h-[calc(100dvh-var(--header-h,4rem)-var(--banner-h,0px)-1.5rem)]">
            <FeedPreview
              matched={matched}
              error={error}
              form={form}
              enterpriseLogo={effectiveLogo}
              onQrPositionChange={(p, ratio) =>
                set(ratio === '9:16' ? 'qrPositionVertical' : 'qrPosition', p)
              }
            />
          </div>
        </div>
        )}
      </div>
    </AppShell>
  );
}




function FeedPreview({
  matched,
  error,
  form,
  enterpriseLogo,
  onQrPositionChange,
}: {
  matched: ListingListResponse['items'] | null;
  error: string | null;
  form: FeedForm;
  enterpriseLogo: string | null;
  onQrPositionChange: (
    p: { x: number; y: number } | null,
    ratio: BannerRatio,
  ) => void;
}) {
  const effectiveLogoForPreview = enterpriseLogo;

  // "Edit template" mode: the top card becomes editable and the QR can be
  // dragged. Only the QR moves — it sits on its own white plate, so it is
  // the one mark that doesn't depend on the scrim for legibility.
  const [editing, setEditing] = React.useState(false);
  const [draftQrPosition, setDraftQrPosition] = React.useState<
    { x: number; y: number } | null
  >(form.qrPosition);
  const editCardRef = React.useRef<HTMLDivElement>(null);

  // Which canvas the preview shows. Purely a viewing control — the feed
  // renders and emits BOTH ratios regardless of what is selected here, so
  // this never reaches the payload. It exists so the ratios can be checked
  // against each other before publishing, which matters most for the two
  // things that actually move between them: where the scrim leaves the
  // photograph, and whether the QR still clears the price.
  const [previewRatio, setPreviewRatio] = React.useState<BannerRatio>('1:1');
  const [fullPreview, setFullPreview] = React.useState(false);

  // The QR is placed per ratio: each canvas stores its own position, and
  // "Move QR code" edits whichever one is on screen.
  const savedQrPosition =
    previewRatio === '9:16' ? form.qrPositionVertical : form.qrPosition;

  // Exactly the props the headless export will build, from exactly the
  // same function — see packages/shared/src/feedBrand.ts. Anything mapped
  // by hand here is a chance for the preview to lie about the export.
  const templateBrand = React.useMemo(
    () =>
      feedBrandToTemplateBrand(feedInputFromForm(form).brand, {
        agencyLogo: enterpriseLogo,
      }),
    [form, enterpriseLogo],
  );

  // The preview needs uploaded faces registered too; the export gets them
  // via the payload's `fonts` array.
  useUploadedFonts([form.mainFontFace, form.secondaryFontFace]);

  // Re-sync the draft whenever the saved value, the template or the ratio
  // changes. It must read the ACTIVE ratio's position: resetting to the
  // square's value here is what made a saved 9:16 drag snap straight back.
  React.useEffect(() => {
    setDraftQrPosition(savedQrPosition);
  }, [savedQrPosition, form.template, previewRatio]);

  React.useEffect(() => {
    if (!form.qrEnabled) setEditing(false);
  }, [form.qrEnabled]);

  // Turning the vertical off hides its tab, so a preview left on 9:16
  // would be stranded on a ratio with no control to leave it.
  React.useEffect(() => {
    if (!form.includeVertical || form.template === 'listing-lookbook') {
      setPreviewRatio('1:1');
    }
  }, [form.includeVertical, form.template]);

  const dirty =
    JSON.stringify(draftQrPosition) !== JSON.stringify(savedQrPosition);

  // Where the handle sits before anything has been dragged.

  // Geometry the drag handle needs. Which hero applies depends on the
  // edited card's own photo count: a listing with fewer than three
  // secondary shots renders full-bleed. See lib/qrDrag.ts for the maths.
  const editCardGallery = ((matched ?? [])[0]?.gallery ?? []).length;
  const qrGeo = {
    qrSize: form.qrSize,
    canvasH:
      form.template === 'listing-lookbook' ? 1080 : bannerHeight(previewRatio),
    heroH:
      form.template === 'listing-lookbook'
        ? 1080
        : bannerHeroHeight(previewRatio, editCardGallery),
  };

  const qrDefaultPos =
    form.template === 'listing-lookbook'
      ? LOOKBOOK_QR_DEFAULT_POS
      : // Must match what the template draws for an untouched code, or the
        // handle starts somewhere the QR isn't.
        bannerQrDefault(previewRatio, form.qrSize, 'catalog', editCardGallery);

  // Drag in normalised space so the saved value holds at any export size.
  const startQrDrag = (e: React.PointerEvent) => {
    const card = editCardRef.current;
    if (!card) return;
    e.preventDefault();
    const rect = card.getBoundingClientRect();
    const move = (ev: PointerEvent) => {
      setDraftQrPosition(
        qrFromPointer(ev.clientX, ev.clientY, rect, qrGeo),
      );
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  // The banner is a fixed 1080 canvas scaled to fit, so it needs the real
  // column width rather than a percentage.
  const gridRef = React.useRef<HTMLDivElement>(null);
  const [tileWidth, setTileWidth] = React.useState(220);
  React.useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const measure = () => {
      // Inset from the panel edges so the creatives sit centred with a
      // margin rather than running edge to edge. One cap for both ratios:
      // the vertical simply runs taller at the same width, so switching
      // between them doesn't resize the column.
      const w = Math.min(el.clientWidth - 32, 420);
      if (w > 0) setTileWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [matched, previewRatio]);

  return (
    <Card className="flex h-full min-h-[640px] flex-col overflow-hidden p-0 lg:min-h-0">
      <div className="relative flex shrink-0 items-center justify-between gap-3 border-b border-[hsl(var(--hairline))] px-5 py-4">
        <div className="flex items-center gap-3">
          {/* Ratio switcher. Shown only when there is genuinely something
              to switch between: the lookbook has no vertical canvas, and
              a feed with 9:16 turned off never exports one — offering the
              tab in either case previews a creative that doesn't ship. */}
          {form.template !== 'listing-lookbook' &&
            form.includeVertical &&
            !editing && (
            <div
              role="radiogroup"
              aria-label="Preview aspect ratio"
              className="flex h-8 items-center gap-0.5 rounded-md border border-[hsl(var(--hairline))] bg-muted/30 p-0.5"
            >
              {BANNER_RATIOS.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={previewRatio === r}
                  title={BANNER_RATIO_LABEL[r]}
                  onClick={() => setPreviewRatio(r)}
                  className={cn(
                    'flex h-full items-center rounded-[4px] px-2.5 font-mono text-[10px] tabular-nums transition-colors',
                    previewRatio === r
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
        </div>

        {editing ? (
          <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-2">
            <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Drag the QR
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setDraftQrPosition(null);
                onQrPositionChange(null, previewRatio);
              }}
            >
              Reset
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setDraftQrPosition(savedQrPosition);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!dirty}
              onClick={() => {
                onQrPositionChange(draftQrPosition, previewRatio);
                setEditing(false);
              }}
            >
              Save
            </Button>
          </div>
        ) : (
          <>
            {form.qrEnabled && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="absolute left-1/2 -translate-x-1/2"
                onClick={() => setEditing(true)}
              >
                <Move />
                Move QR code
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setFullPreview(true)}
              disabled={!matched?.length}
            >
              <Maximize2 />
              Full preview
            </Button>
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <div className="p-5 font-mono text-[11px] text-destructive">
            Couldn't load listings: {error}
          </div>
        ) : matched === null ? (
          <div className="p-5 font-mono text-[11px] text-muted-foreground">
            Loading…
          </div>
        ) : matched.length === 0 ? (
          <div className="p-8 text-center font-mono text-[11px] text-muted-foreground">
            No listings match these filters — the feed would be empty.
          </div>
        ) : (
          // Each listing rendered through the selected template, so the
          // preview shows the actual creative rather than a raw photo.
          <div ref={gridRef} className="flex flex-col items-center gap-4 p-4">
            {matched.map((l, i) => (
              <div
                key={l._id}
                ref={i === 0 ? editCardRef : undefined}
                className={cn(
                  'relative',
                  editing && i === 0 && 'ring-2 ring-primary ring-offset-2 ring-offset-card rounded-[28px]',
                )}
              >
              {/* Drag handle overlays the QR on the editable card. The
                  template renders the code itself; this is just the hit
                  area, sized and placed to match. */}
              {editing && i === 0 && form.qrEnabled && (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Drag to reposition the permit QR"
                  onPointerDown={startQrDrag}
                  className="absolute z-10 cursor-grab rounded-md ring-2 ring-primary active:cursor-grabbing"
                  style={qrHandleStyle(draftQrPosition ?? qrDefaultPos, qrGeo)}
                />
              )}
              {form.template === 'listing-lookbook' ? (
              <ListingLookbook
                width={tileWidth}
                brand={{
                  ...LOOKBOOK_DEFAULT_BRAND,
                  // Main colour reaches the status tag only — the rest of
                  // the design stays achromatic per the handoff.
                  ...templateBrand,
                  // Preview-only: rounded in the panel, square on export.
                  radius: 28,
                  // Live drag feedback on the editable card only.
                  qrPosition: i === 0 ? draftQrPosition : form.qrPosition,
                }}
                listing={{
                  imageUrl: l.image ?? null,
                  imageAlt: l.title ?? '',
                  locality: l.area ?? '',
                  city: l.city ?? null,
                  price: l.price ?? null,
                  currency: l.currency ?? 'AED',
                  locale: 'en-AE',
                  beds: l.bedrooms == null ? null : String(l.bedrooms),
                  baths: l.bathrooms == null ? null : String(l.bathrooms),
                  area: null,
                  areaUnit: 'sqft',
                  // A feed is not a carousel, so no counter — the tag
                  // takes the corner instead.
                  counter: null,
                  badge: l.forSale ? 'FOR SALE' : 'FOR RENT',
                  permitQr: l.permitQr ?? null,
                }}
              />
              ) : (
              <ListingBanner
                width={tileWidth}
                ratio={previewRatio}
                brand={{
                  ...DEFAULT_BRAND,
                  // `scrim` gets the secondary colour, which tints the
                  // bottom gradient and the frame behind the photo so a
                  // failed image still matches.
                  ...templateBrand,
                  // Preview-only: rounded in the panel, square on export.
                  radius: 28,
                  // Live drag feedback on the editable card only.
                  qrPosition: i === 0 ? draftQrPosition : savedQrPosition,
                }}
                listing={bannerListingFrom(l)}
              />
              )}
              </div>
            ))}
          </div>
        )}
      </div>

      <FeedFullPreview
        open={fullPreview}
        onClose={() => setFullPreview(false)}
        listings={matched ?? []}
        brand={templateBrand}
        qrPosition={form.qrPosition}
        qrPositionVertical={form.qrPositionVertical}
        includeVertical={
          form.includeVertical && form.template !== 'listing-lookbook'
        }
      />

      <div className="shrink-0 border-t border-[hsl(var(--hairline))] px-5 py-3">
        <div className="font-mono text-[10px] text-muted-foreground">
          {FORMAT_OPTIONS.find((f) => f.value === form.format)?.label}
        </div>
      </div>
    </Card>
  );
}
